import {cpServiceGet} from 'api';
import {sendSentryException} from 'utils/error-handling';
import dayjs from 'utils/dayjs';
import {camelizeKeys} from 'utils/format-strings';
import {type RawApiResponse} from 'api/api.types';
import {
    type AggregatedRating,
    type CityRating,
    type MicroclimateRatings,
    type StarRatingPeriod,
} from 'pages/facilities/driver-feedback/star-ratings/StarRatings.types';

export interface GetStarRatingsResponse {
    aggregatedRatings: AggregatedRating[];
    cityRatings: CityRating[];
    microclimateRatings: MicroclimateRatings[];
    end: string;
    facilityId: number;
    start: string;
}

export type GetStarRatingsRawResponse = RawApiResponse<GetStarRatingsResponse>;

/**
 * Formats the request parameters for getting star ratings based on a selected period.
 *
 * @param  selectedPeriod - The period for which the star ratings are requested.
 *
 * @memberof module:api/facilities/dto
 */
export const formatGetStarRatingsRequest = (
    selectedPeriod: StarRatingPeriod
) => {
    const dateTo = dayjs().format('YYYY-MM-DD');
    // we subtract 1 from the value to ensure that we're including the current month/day/year in the lookback period
    const dateFrom = dayjs()
        .subtract(selectedPeriod.value - 1, selectedPeriod.unit)
        .format('YYYY-MM-DD');

    return {
        dateTo,
        dateFrom,
        unit: selectedPeriod.unit,
    };
};

/**
 * Formats the response from the star ratings API.
 *
 * @function
 * @param response - The response object from the star ratings API.
 *
 * @memberof module:api/facilities/dto
 */
export const formatGetStarRatingsRepsonse = (
    response: GetStarRatingsRawResponse
): GetStarRatingsResponse => {
    const formattedResponse = camelizeKeys(response);

    return formattedResponse;
};

/**
 * Gets a facility's aggregated star ratings within a date range
 *
 * @param  facilityId
 * @param  selectedPeriod - The period for which the star ratings are requested.
 *
 * @see '/control-panel-service/v1/facilities/{facilityId}/star-ratings'
 *
 */
const getStarRatings = async (
    facilityId: number,
    selectedPeriod: StarRatingPeriod
) => {
    try {
        const {dateTo, dateFrom, unit} =
            formatGetStarRatingsRequest(selectedPeriod);

        const response = await cpServiceGet<GetStarRatingsRawResponse>(
            `facilities/${facilityId}/star-ratings/?dateFrom=${dateFrom}&dateTo=${dateTo}&unit=${unit}`
        );

        const formattedResponse = formatGetStarRatingsRepsonse(
            response.data.data
        );

        return formattedResponse;
    } catch (err) {
        sendSentryException(err);

        throw new Error(err.message);
    }
};

export default getStarRatings;
